import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { toast } from "sonner";
import { Mic, MicOff, Loader2, Globe } from "lucide-react";
import { VOICE_LANGUAGES } from "@/lib/voiceLanguages";
import FloatingSlot from "@/components/FloatingSlot";

// Wake-word matcher: `Hey AI Find`, `Hi AIFind`, `Ok AI find`, `AlIFind` etc.
// Keep loose — SpeechRecognition transcripts have varied punctuation/spelling.
const WAKE_RX = /\b(hey|hi|ok|okay)?\s*a\.?\s*[il1]?\.?\s*find\b/i;

// Intent router: match phrases that signal the user wants to POST a listing,
// not search for one. Kept intentionally loose — transcripts are noisy.
//   "post a plumber service" / "post an ad for..." / "list my shop..."
//   "I am a plumber in Andheri" / "I'm a doctor" / "we are a restaurant"
//   "add a listing for tuition" / "sell my old bike"
const LISTING_INTENT_RX = /\b(post\s+(?:a|an|my)|list\s+(?:a|my)|add\s+(?:a|my)|create\s+(?:a|my)|sell\s+my|rent\s+my|i(?:'|\s+a)m\s+(?:a|an)|i\s+am\s+(?:a|an)|we(?:'|\s+a)re\s+(?:a|an)|we\s+are\s+(?:a|an))\b/i;

// Wallet intent router: money-related commands that should hit the wallet parser
// (regex-only, deterministic) instead of the fuzzy search classifier.
//   "show my wallet balance" / "top up 500 rupees" / "add 1000 to my wallet"
//   "convert to gold" / "buy silver" / "how much gold do I have"
//   "withdraw 200" / "cash out" / "send 500 to Rajesh" (detected → replies "not yet supported")
const WALLET_INTENT_RX = /\b(wallet|balance|top[\s-]?up|add\s+money|load\s+wallet|recharge\s+wallet|withdraw|cash[\s-]?out|payout|convert\s+.*(gold|silver)|buy\s+(gold|silver)|(gold|silver)\s+holdings?|how\s+much\s+(gold|silver)|my\s+(gold|silver|holdings))\b/i;

function getSpeech() {
  return typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
}

export default function VoiceSearchMic() {
  const [state, setState] = useState("idle"); // idle | listening | processing | wake
  const [lang, setLang] = useState(() =>
    (typeof window !== "undefined" && localStorage.getItem("f2n.voice.lang")) || "auto"
  );
  const [wakeOn, setWakeOn] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("f2n.voice.wake") === "1"
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [heardHint, setHeardHint] = useState("");   // "last heard" indicator for wake-mode feedback
  const [inputFocused, setInputFocused] = useState(false);
  const recRef = useRef(null);
  const wakeRecRef = useRef(null);
  const restartCountRef = useRef(0);
  const lastRestartRef = useRef(0);
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { loc } = useLocation();
  const SR = getSpeech();
  const supported = !!SR;

  // Routes that have their own bottom input bar (chat / messenger) — hide the floating
  // mic entirely so it doesn't overlap the send button + language picker of that page.
  const HIDDEN_ROUTES = ["/messages", "/messenger", "/chat", "/thread", "/live-world"];
  const routeSuppressed = HIDDEN_ROUTES.some(p => routerLocation.pathname.startsWith(p));

  const effectiveLang = lang === "auto" ? (navigator.language || "en-IN") : lang;

  const speak = (text) => new Promise((resolve) => {
    if (!text || typeof window === "undefined" || !("speechSynthesis" in window)) return resolve();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.lang = effectiveLang;
    u.onend = resolve;
    u.onerror = resolve;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    // Safety timeout — some browsers never fire onend for short utterances.
    setTimeout(resolve, Math.min(6000, 800 + text.length * 60));
  });

  const runCommand = async (transcript) => {
    setState("processing");
    try {
      const stripped = transcript.replace(WAKE_RX, " ").trim();
      // Wallet intent takes precedence over listing intent — "top up ₹500" should
      // never be routed to /post as a listing draft. If it doesn't match wallet,
      // fall through to listing, then to search.
      if (WALLET_INTENT_RX.test(stripped)) {
        const { data } = await api.post("/voice-search/parse-wallet", {
          transcript, lang: effectiveLang,
        });
        await speak(data.spoken_reply);
        if (data.redirect_url) {
          navigate(data.redirect_url, { state: data.intent_state ? { voice_intent: data.intent_state } : undefined });
        }
        return;
      }
      // Intent router: if the transcript sounds like the user is posting/listing
      // something (not searching), route to the parse-listing endpoint and hand
      // off to /post with the draft pre-filled for review.
      if (LISTING_INTENT_RX.test(stripped)) {
        const { data } = await api.post("/voice-search/parse-listing", {
          transcript, lat: loc?.lat, lng: loc?.lng, lang: effectiveLang,
        });
        await speak("Opening the post form with your details — please review and publish.");
        navigate("/post", { state: { voice_draft: data.draft, voice_transcript: data.raw_transcript } });
        return;
      }
      const { data } = await api.post("/voice-search/parse", {
        transcript, lat: loc?.lat, lng: loc?.lng, lang: effectiveLang,
      });
      // Speak the extended reply (which includes top 3 nearby results) then navigate.
      const line = data.spoken_reply_extended || data.spoken_reply;
      await speak(line);
      navigate(data.redirect_url);
    } catch { toast.error("Could not understand — try again."); }
    finally { setState(wakeOn ? "wake" : "idle"); }
  };

  const doOneShotListen = () => {
    if (!SR) return toast.error("Your browser doesn't support voice input");
    if (state !== "idle" && state !== "wake") return;
    const rec = new SR();
    rec.lang = effectiveLang;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (evt) => runCommand(evt.results[0][0].transcript);
    rec.onend = () => setState(wakeOn ? "wake" : "idle");
    rec.onerror = () => setState(wakeOn ? "wake" : "idle");
    setState("listening");
    try { rec.start(); } catch { setState("idle"); }
    recRef.current = rec;
    toast.info(`Listening in ${effectiveLang} — say "Hey AI Find, find a water bottle near me"`);
  };

  // Continuous wake-word listener — Alexa-like ambient trigger.
  //
  // Reliability notes:
  //   - Browsers force-stop SpeechRecognition after ~60s of silence; we auto-restart on
  //     natural `onend` and on the `no-speech` error, but NOT on `aborted` / `audio-capture`
  //     which typically indicate the user revoked the mic or another tab captured it.
  //   - We rate-limit restarts (max 1 per 800ms) so a broken driver can't spin the CPU.
  //   - We use a confidence gate (>0.55) on the wake match to reduce false positives from
  //     background chatter — WAKE_RX alone was firing on "I find" during phone calls.
  useEffect(() => {
    if (!wakeOn || !SR) return;
    let cancelled = false;
    const rec = new SR();
    wakeRecRef.current = rec;
    rec.lang = effectiveLang;
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (evt) => {
      const last = evt.results[evt.results.length - 1];
      const alt = last[0];
      const text = alt.transcript;
      if (!last.isFinal) return;
      // Confidence gate — some browsers (Firefox) don't report confidence; treat undefined as 1.
      const conf = typeof alt.confidence === "number" ? alt.confidence : 1;
      if (WAKE_RX.test(text) && conf >= 0.55) {
        setHeardHint(text.trim().slice(0, 40));
        setTimeout(() => setHeardHint(""), 3500);
        try { rec.stop(); } catch {}
        runCommand(text);
      }
    };

    const scheduleRestart = () => {
      if (cancelled || !wakeOn) return;
      const now = Date.now();
      if (now - lastRestartRef.current < 800) {
        restartCountRef.current += 1;
        // If we're restart-storming (>5 restarts in <5s), the driver is broken — bail.
        if (restartCountRef.current > 5) {
          console.warn("[voice-mic] restart storm detected — disabling wake mode");
          setWakeOn(false);
          toast.error("Wake mode paused — your browser is throttling the mic. Tap the mic to talk.");
          return;
        }
      } else {
        restartCountRef.current = 0;
      }
      lastRestartRef.current = now;
      setTimeout(() => {
        if (cancelled || !wakeOn) return;
        try { rec.start(); } catch {}
      }, 250);
    };

    rec.onend = scheduleRestart;
    rec.onerror = (e) => {
      const err = e?.error;
      if (err === "not-allowed") {
        setWakeOn(false);
        toast.error("Mic permission denied — enable it in your browser to use wake mode.");
        return;
      }
      if (err === "aborted" || err === "audio-capture") {
        // Another app / tab grabbed the mic. Stop trying — user can re-enable.
        setWakeOn(false);
        return;
      }
      // "no-speech" / "network" — safe to retry.
    };
    try { rec.start(); setState("wake"); } catch {}
    return () => {
      cancelled = true;
      try { rec.stop(); } catch {}
      restartCountRef.current = 0;
      setState("idle");
    };
  }, [wakeOn, effectiveLang, SR]); // eslint-disable-line

  // Auto-hide the floating mic while the user is typing into a text field / textarea.
  // Otherwise it collides with the on-screen keyboard on mobile and the review/comment
  // forms across the app (ListingDetail, Feed post composer, chat inputs).
  useEffect(() => {
    const onFocus = (e) => {
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || "").toLowerCase();
      const editable = t.isContentEditable;
      if (tag === "input" || tag === "textarea" || editable) setInputFocused(true);
    };
    const onBlur = () => setInputFocused(false);
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    return () => {
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
    };
  }, []);

  useEffect(() => { localStorage.setItem("f2n.voice.wake", wakeOn ? "1" : "0"); }, [wakeOn]);
  useEffect(() => { localStorage.setItem("f2n.voice.lang", lang); }, [lang]);

  if (!supported || inputFocused || routeSuppressed) return null;

  const busy = state === "listening" || state === "processing";
  const active = state === "listening" || state === "wake";
  const langLabel = VOICE_LANGUAGES.find((l) => l.code === lang)?.label || "Auto";

  return (
    <FloatingSlot
      zone="bottom-right"
      order={1}
      size="cluster"
      zIndex="fab-secondary"
      className="flex flex-col items-end gap-2"
      testid="voice-mic-root"
    >
      {heardHint && (
        <div className="max-w-[220px] text-xs font-semibold px-3 py-1.5 rounded-2xl bg-white/95 border border-emerald-200 text-emerald-900 shadow-md" data-testid="voice-heard-hint">
          Heard: <span className="font-normal">"{heardHint}"</span>
        </div>
      )}
      {pickerOpen && (
        <div className="w-72 max-h-80 overflow-y-auto rounded-xl shadow-xl bg-white border border-stone-200 p-2 text-sm" data-testid="voice-lang-picker">
          {VOICE_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setPickerOpen(false); toast.success(`Voice language: ${l.label}`); }}
              className={`w-full text-left px-3 py-1.5 rounded-md hover:bg-stone-100 ${l.code === lang ? "bg-stone-100 font-semibold" : ""}`}
              data-testid={`voice-lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
      {wakeOn && !pickerOpen && (
        <div className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
          Listening · {langLabel.split("·")[0].trim()}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setPickerOpen((o) => !o)}
          title={`Voice language: ${langLabel}`}
          className="w-10 h-10 rounded-full flex items-center justify-center shadow-md bg-white text-stone-600 border border-stone-200"
          data-testid="voice-lang-toggle"
        >
          <Globe size={16} />
        </button>
        <button
          onClick={() => setWakeOn((w) => !w)}
          title={wakeOn ? "Stop wake-word listener" : "Start wake-word listener"}
          className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${wakeOn ? "bg-emerald-500 text-white" : "bg-white text-stone-500 border border-stone-200"}`}
          data-testid="voice-wake-toggle"
        >
          {wakeOn ? <Mic size={16} /> : <MicOff size={16} />}
        </button>
        <button
          onClick={doOneShotListen}
          disabled={busy}
          title='Tap and speak — "Hey AI Find, find a water bottle near me"'
          className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${active ? "bg-red-500 text-white animate-pulse" : "bg-[#E05E36] text-white hover:bg-[#c94e2a]"}`}
          data-testid="voice-mic-btn"
        >
          {state === "processing" ? <Loader2 size={22} className="animate-spin" /> : <Mic size={22} />}
        </button>
      </div>
    </FloatingSlot>
  );
}
